import { useCallback, useEffect, useRef, useState } from "react";
import {
  FACEBOOK_OAUTH_ERROR_TYPE,
  FACEBOOK_OAUTH_SUCCESS_TYPE,
  facebookOauthPopupMessageSchema,
  type FacebookOauthPopupMessage,
} from "@multi-chat/shared";
import { API_BASE_URL } from "../../api/client.js";
import { openCenteredPopup } from "../../lib/open-centered-popup.js";

export type FacebookPopupStatus =
  | { kind: "idle" }
  | { kind: "opening" }
  | { kind: "blocked" }
  | { kind: "closed" }
  | { kind: "success" }
  | { kind: "error"; code: string };

interface UseFacebookPopupResult {
  status: FacebookPopupStatus;
  start: (organizationId: number) => void;
  reset: () => void;
}

const POPUP_NAME = "multi_chat_facebook_connect";
const POPUP_SIZE = { width: 600, height: 750 };

function originMatches(origin: string): boolean {
  try {
    return new URL(origin).origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
}

export function useFacebookPopup(onSuccess: () => void): UseFacebookPopupResult {
  const [status, setStatus] = useState<FacebookPopupStatus>({ kind: "idle" });
  const popupRef = useRef<Window | null>(null);
  const closedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clearClosedTimer = useCallback(() => {
    if (closedTimerRef.current) {
      clearInterval(closedTimerRef.current);
      closedTimerRef.current = null;
    }
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!originMatches(event.origin)) return;
      const parsed = facebookOauthPopupMessageSchema.safeParse(event.data);
      if (!parsed.success) return;

      const message: FacebookOauthPopupMessage = parsed.data;
      if (message.type === FACEBOOK_OAUTH_SUCCESS_TYPE) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        popupRef.current?.close();
        popupRef.current = null;
        setStatus({ kind: "success" });
        onSuccessRef.current();
      } else if (message.type === FACEBOOK_OAUTH_ERROR_TYPE) {
        clearClosedTimer();
        window.removeEventListener("message", handleMessage);
        popupRef.current?.close();
        popupRef.current = null;
        setStatus({ kind: "error", code: message.code });
      }
    },
    [clearClosedTimer],
  );

  const reset = useCallback(() => {
    clearClosedTimer();
    window.removeEventListener("message", handleMessage);
    popupRef.current?.close();
    popupRef.current = null;
    setStatus({ kind: "idle" });
  }, [clearClosedTimer, handleMessage]);

  const start = useCallback(
    (organizationId: number) => {
      setStatus({ kind: "opening" });
      window.addEventListener("message", handleMessage);

      const url = `${API_BASE_URL}/organizations/${organizationId}/facebook/connect/start`;
      const popup = openCenteredPopup(url, POPUP_NAME, POPUP_SIZE);

      if (!popup) {
        window.removeEventListener("message", handleMessage);
        setStatus({ kind: "blocked" });
        return;
      }

      popupRef.current = popup;

      clearClosedTimer();
      closedTimerRef.current = setInterval(() => {
        if (popup.closed) {
          clearClosedTimer();
          window.removeEventListener("message", handleMessage);
          popupRef.current = null;
          setStatus((current) =>
            current.kind === "success" || current.kind === "error"
              ? current
              : { kind: "closed" },
          );
        }
      }, 500);
    },
    [handleMessage, clearClosedTimer],
  );

  useEffect(() => {
    return () => {
      clearClosedTimer();
      window.removeEventListener("message", handleMessage);
    };
  }, [clearClosedTimer, handleMessage]);

  return { status, start, reset };
}
